import React from "react";
import { FiExternalLink } from "react-icons/fi";

const PremiumTypingAnimation = () => {
  return (
    <div className="flex items-center">
      <div className="flex items-center space-x-1.5">
        <div
          className="w-1.5 h-1.5 bg-gray-400/70 rounded-full animate-pulse"
          style={{ animationDuration: "1s", animationDelay: "0ms" }}
        ></div>
        <div
          className="w-1.5 h-1.5 bg-gray-400/70 rounded-full animate-pulse"
          style={{ animationDuration: "1s", animationDelay: "300ms" }}
        ></div>
        <div
          className="w-1.5 h-1.5 bg-gray-400/70 rounded-full animate-pulse"
          style={{ animationDuration: "1s", animationDelay: "600ms" }}
        ></div>
      </div>
    </div>
  );
};

const SearchStages = ({ searchInfo }) => {
  if (!searchInfo || !searchInfo.stages || searchInfo.stages.length === 0)
    return null;

  return (
    <div className="mb-3 mt-1 relative px-4">
      {/* Search Process UI */}
      <div className="flex flex-col space-y-4 text-sm text-gray-700">
        {/* Searching Stage */}
        {searchInfo.stages.includes("searching") && (
          <div className="relative">
            {/* Green dot */}
            <div className="absolute -left-[7px] top-2 w-[8px] h-[8px] bg-teal rounded-full z-10 shadow-sm"></div>

            {/* Connecting line to next item if reading exists */}
            {searchInfo.stages.includes("reading") && (
              <div className="absolute -left-[4px] top-3 w-0.5 h-[calc(100%+1rem)] bg-gradient-to-b from-teal to-teal/5"></div>
            )}

            <div className="flex flex-col">
              <span className="font-light mb-2 ml-2 text-teal">
                Exploring the web for insights…
                {searchInfo.stages.includes("reading") ? (
                  <span className="text-[9px] ml-3 italic text-white tracking-widest">
                    Done searching
                  </span>
                ) : (
                  ""
                )}
              </span>

              {/* Search Query in box styling */}
              <div className="flex flex-wrap gap-2 pl-2 mt-1">
                <div className="bg-gray-100 text-xs px-3 py-1.5 rounded inline-flex items-center">
                  <svg
                    className="w-3 h-3 mr-1.5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    ></path>
                  </svg>
                  {searchInfo.query}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reading Stage */}
        {searchInfo.stages.includes("reading") && (
          <div className="relative">
            {/* Green dot */}
            <div className="absolute -left-[7px] top-2 w-[8px] h-[8px] bg-teal rounded-full z-10 shadow-sm"></div>
            {/* Connecting line to next item if writing exists */}
            {searchInfo.stages.includes("writing") && (
              <div className="absolute -left-[4px] top-3 w-0.5 h-[calc(100%+1rem)] bg-gradient-to-b from-teal to-teal/5"></div>
            )}

            <div className="flex flex-col">
              <span className="font-light mb-2 ml-2 text-teal">
                Making sense of the information…
                {searchInfo.stages.includes("writing") ? (
                  <span className="text-[9px] ml-3 italic text-white tracking-widest">
                    Done reading
                  </span>
                ) : (
                  ""
                )}
              </span>

              {/* Search Results */}
              {searchInfo.urls && searchInfo.urls.length > 0 && (
                <div className="pl-2 space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(searchInfo.urls) ? (
                      searchInfo.urls.map((url, index) => (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          key={index}
                          className="bg-gray-100 text-xs px-3 py-1.5 rounded truncate max-w-[200px] transition-all duration-200 hover:bg-gray-50"
                        >
                          {typeof url === "string"
                            ? url
                            : JSON.stringify(url).substring(0, 30)}
                        </a>
                      ))
                    ) : (
                      <a
                        href={searchInfo.urls}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gray-100 text-xs px-3 py-1.5 rounded truncate max-w-[200px] transition-all duration-200 hover:bg-gray-50"
                      >
                        {typeof searchInfo.urls === "string"
                          ? searchInfo.urls.substring(0, 30)
                          : JSON.stringify(searchInfo.urls).substring(0, 30)}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Writing Stage */}
        {searchInfo.stages.includes("writing") && (
          <div className="relative">
            {/* Green dot with subtle glow effect */}
            <div className="absolute -left-[7px] top-2 w-[8px] h-[8px] bg-teal rounded-full z-10 shadow-sm"></div>
            <span className="text-teal pl-2 font-light">
              Completed gathering information and crafting response.
            </span>
          </div>
        )}

        {/* Error Message */}
        {searchInfo.stages.includes("error") && (
          <div className="relative">
            {/* Red dot over the vertical line */}
            <div className="absolute -left-3 top-1 w-2.5 h-2.5 bg-red-400 rounded-full z-10 shadow-sm"></div>
            <span className="font-medium">Search error</span>
            <div className="pl-4 text-xs text-red-500 mt-1">
              {searchInfo.error || "An error occurred during search."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function linkifyMarkdown(text) {
  const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  return text.split("\n").map((line, idx) => {
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(line)) !== null) {
      const [fullMatch, linkText, url] = match;
      const start = match.index;

      // Push text before the link
      if (start > lastIndex) {
        parts.push(line.slice(lastIndex, start));
      }

      // Push the link
      parts.push(
        <a
          key={idx + start}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-teal hover:text-teal/80 transition-colors duration-500 flex items-center gap-[4px]"
        >
          {linkText} <FiExternalLink className="text-sm mb-[4px]" />
        </a>
      );

      lastIndex = start + fullMatch.length;
    }

    // Push remaining text
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }

    return (
      <p key={idx} className="mb-3 flex flex-row items-center">
        {parts}
      </p>
    );
  });
}

const LanggraphChatView = ({ messages, allResponses, bottomRef }) => {
  return (
    <div className="h-fit w-full pt-5 px-4 overflow-auto no-scrollbar text-base leading-7 tracking-wide text-white/50 font-saira">
      {messages?.map((message, ind) => (
        <React.Fragment key={message.id}>
          <div
            className={`flex ${
              message.isUser ? "justify-end" : "justify-start"
            } mb-5`}
          >
            <div className="flex flex-col max-w-2xl sm:max-w-xl lg:max-w-2xl">
              {/* Search Status Display - Now ABOVE the message */}
              {!message.isUser && message.searchInfo && (
                <SearchStages searchInfo={message.searchInfo} />
              )}
              {/* Message Content */}
              <div
                className={`${
                  message.isUser
                    ? "bg-white/5 w-fit px-2 py-1 rounded-lg rounded-br-none items-end ml-auto"
                    : "text-white/50"
                }`}
              >
                {message.isLoading ? (
                  <PremiumTypingAnimation />
                ) : (
                  (
                    <div className="font-thin text-white/70 whitespace-pre-wrap">
                      {linkifyMarkdown(
                        message.content
                          .replace(/\*\*(.*?)\*\*/g, "$1")
                          .replace(/\*(.*?)\*/g, "$1")
                          .replace(/`(.*?)`/g, "$1")
                          .replace(/###\s*/g, "")
                      )}
                    </div>
                  ) || (
                    // Fallback if content is empty but not in loading state
                    <span className="text-gray-400 text-xs italic">
                      Waiting for response...
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
          {allResponses?.prompts && allResponses?.prompts[ind] && (
            <div className="w-full flex items-end pb-5">
              <p className="bg-white/5 w-[80%] sm:w-fit max-w[80%] sm:max-w-[70%] text-end px-4 py-1 rounded-lg rounded-br-none items-end ml-auto">
                {allResponses?.prompts[ind]}
              </p>
            </div>
          )}
        </React.Fragment>
      ))}
      <div ref={bottomRef} />
    </div>
  );
};

export default LanggraphChatView;
